import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Heart } from 'lucide-react';
import { Button } from './ui/button';

const LOGO_URL = '/assets/images/logo.png';

const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/about', label: 'About' },
  { href: '/impact', label: 'Impact Ecosystem' },
  { href: '/programs', label: 'Programs' },
  { href: '/partnership', label: 'Partnership' },
  { href: '/blog', label: 'Blog' },
  { href: '/contact', label: 'Contact' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 transition-all duration-300 ${
        isScrolled ? 'shadow-sm py-3' : 'py-4'
      }`}
      data-testid="main-header"
    >
      <div className="container-custom">
        <div className="flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 min-w-0" data-testid="header-logo">
            <img
              src={LOGO_URL}
              alt="His Presence Chapel"
              className="h-12 w-12 rounded-full object-cover border border-slate-200 bg-white"
            />
            <div className="min-w-0">
              <span className="block font-heading font-semibold text-lg leading-tight text-[#2D3E55]">
                His Presence Chapel
              </span>
              <span className="hidden sm:block text-xs text-slate-500">
                Faith in Action. Structure for Impact.
              </span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => {
              const active = location.pathname === link.href;

              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`font-body text-sm font-medium transition-colors ${
                    active
                      ? 'text-[#F15A24]'
                      : 'text-[#2D3E55] hover:text-[#F15A24]'
                  }`}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link to="/donate">
              <Button
                className="bg-[#F15A24] hover:bg-[#d64d1e] text-white font-body font-medium"
                data-testid="cta-donate-btn"
              >
                <Heart size={16} className="mr-2" />
                Donate Now
              </Button>
            </Link>
          </div>

          <button
            className="lg:hidden p-2 text-[#2D3E55]"
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <nav
            className="lg:hidden mt-4 pb-4 border-t border-slate-200 pt-4 bg-white animate-fadeIn"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => {
                const active = location.pathname === link.href;

                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`font-body text-base font-medium py-2 transition-colors ${
                      active ? 'text-[#F15A24]' : 'text-[#2D3E55]'
                    }`}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                );
              })}

              <Link to="/donate" className="mt-3">
                <Button className="w-full bg-[#F15A24] hover:bg-[#d64d1e] text-white">
                  <Heart size={16} className="mr-2" />
                  Donate Now
                </Button>
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};